import { useEffect, useMemo, useRef, useState } from "react";
import {
  createQuotePhoneRecaptcha,
  getQuoteInstallationId,
  hasValidQuotePhoneVerification,
  sendQuotePhoneOtp,
  verifyQuotePhoneOtp,
} from "../../services/quotePhoneVerificationService";

export const emptyQuoteContact = (phone = "") => ({
  phone,
  callbackPreference: "",
  callbackPreferenceLabel: "",
  preferredLanguage: "",
  phoneVerified: false,
  deviceInstallationId: "",
});

export const isQuoteContactValid = (value) =>
  /^[6-9]\d{9}$/.test(String(value?.phone || "").replace(/\D/g, "")) &&
  value?.phoneVerified === true &&
  /^[A-Za-z0-9-]{20,80}$/.test(String(value?.deviceInstallationId || "")) &&
  Boolean(value?.callbackPreference) &&
  ["telugu", "hindi", "english"].includes(value?.preferredLanguage);

const timeLabel = (date) =>
  date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });

const callbackOptions = () => {
  const now = new Date();
  const first = new Date(now);
  first.setSeconds(0, 0);
  const remainder = first.getMinutes() % 15;
  first.setMinutes(first.getMinutes() + (remainder === 0 ? 15 : 15 - remainder));

  return [
    { value: "immediately", label: "Immediately" },
    ...Array.from({ length: 12 }, (_, index) => {
      const date = new Date(first.getTime() + index * 15 * 60 * 1000);
      return { value: date.toISOString(), label: timeLabel(date) };
    }),
  ];
};

export default function TailorQuoteContactFields({ value, onChange }) {
  const slots = useMemo(callbackOptions, []);
  const verifierRef = useRef(null);
  const [verificationId, setVerificationId] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const update = (changes) => onChange({ ...value, ...changes });

  useEffect(() => {
    const phone = String(value.phone || "").replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(phone)) return;
    let active = true;
    hasValidQuotePhoneVerification(phone)
      .then((valid) => {
        if (active) update({
          phoneVerified: valid,
          deviceInstallationId: valid ? getQuoteInstallationId() : "",
        });
      })
      .catch(() => {
        if (active) update({ phoneVerified: false, deviceInstallationId: "" });
      });
    return () => { active = false; };
    // Verification is intentionally rechecked only when the entered phone changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.phone]);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => () => verifierRef.current?.clear(), []);

  const sendOtp = async () => {
    setBusy(true);
    setError("");
    try {
      verifierRef.current?.clear();
      verifierRef.current = createQuotePhoneRecaptcha("quote-phone-recaptcha");
      setVerificationId(await sendQuotePhoneOtp(value.phone, verifierRef.current));
      setResendSeconds(30);
    } catch (sendError) {
      setError(sendError?.message || "Unable to send OTP.");
      verifierRef.current?.clear();
      verifierRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setBusy(true);
    setError("");
    try {
      const deviceInstallationId = await verifyQuotePhoneOtp({
        phoneNumber: value.phone,
        verificationId,
        smsCode: otp,
      });
      update({ phoneVerified: true, deviceInstallationId });
      setVerificationId("");
      setOtp("");
    } catch (verifyError) {
      setError(verifyError?.message || "Unable to verify OTP.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
    <div className="grid sm:grid-cols-3 gap-4">
      <label className="text-sm font-semibold">
        Phone number
        <input
          inputMode="numeric"
          maxLength={10}
          value={value.phone}
          onChange={(event) => {
            setVerificationId("");
            setOtp("");
            setError("");
            update({
              phone: event.target.value.replace(/\D/g, ""),
              phoneVerified: false,
              deviceInstallationId: "",
            });
          }}
          className="w-full mt-2 border border-[#D9C9BE] px-4 py-3 bg-white"
          placeholder="10-digit mobile number"
        />
      </label>
      <label className="text-sm font-semibold">
        Preferred call time
        <select
          value={value.callbackPreference}
          onChange={(event) => {
            const selected = slots.find((slot) => slot.value === event.target.value);
            update({
              callbackPreference: event.target.value,
              callbackPreferenceLabel: selected?.label || "",
            });
          }}
          className="w-full mt-2 border border-[#D9C9BE] px-4 py-3 bg-white"
        >
          <option value="">Select call time</option>
          {slots.map((slot) => <option key={slot.value} value={slot.value}>{slot.label}</option>)}
        </select>
      </label>
      <label className="text-sm font-semibold">
        Preferred language
        <select
          value={value.preferredLanguage}
          onChange={(event) => update({ preferredLanguage: event.target.value })}
          className="w-full mt-2 border border-[#D9C9BE] px-4 py-3 bg-white"
        >
          <option value="">Select language</option>
          <option value="telugu">Telugu</option>
          <option value="hindi">Hindi</option>
          <option value="english">English</option>
        </select>
      </label>
    </div>
    <div id="quote-phone-verification" className="mt-4 border border-[#E5D4C8] p-4" aria-live="polite">
      {value.phoneVerified ? (
        <p className="text-sm font-semibold text-green-700">Phone verified for the next 7 days</p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          {verificationId ? (
            <label className="text-sm font-semibold flex-1">
              SMS OTP
              <input
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
                className="w-full mt-2 border border-[#D9C9BE] px-4 py-3 bg-white"
                placeholder="6-digit code"
              />
            </label>
          ) : null}
          <button
            type="button"
            onClick={sendOtp}
            disabled={busy || resendSeconds > 0 || !/^[6-9]\d{9}$/.test(value.phone)}
            className="btn-secondary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "PLEASE WAIT..." : resendSeconds > 0 ? `RESEND IN ${resendSeconds}s` : verificationId ? "RESEND OTP" : "SEND OTP"}
          </button>
          {verificationId ? (
            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy || otp.length !== 6}
              className="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              VERIFY OTP
            </button>
          ) : null}
        </div>
      )}
      {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
      <div id="quote-phone-recaptcha" />
    </div>
    </div>
  );
}
